import { useState, useEffect } from 'react';
import { doc, setDoc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../config/firebase'; // Import your Firestore instance

const useCouponInteraction = (coupon) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [canInteract, setCanInteract] = useState(true);

  useEffect(() => {
    const checkLocalInteraction = () => {
      const now = Date.now();
      const lastInteraction = localStorage.getItem(`last_interaction_${coupon.coupon_id}`);
      const lastInteractionType = localStorage.getItem(`last_interaction_type_${coupon.coupon_id}`);

      if (lastInteraction && now - parseInt(lastInteraction, 10) < 24 * 60 * 60 * 1000) {
        setCanInteract(false);
        if (lastInteractionType === 'like') {
          setIsLiked(true);
          setIsDisliked(false);
        } else if (lastInteractionType === 'dislike') {
          setIsDisliked(true);
          setIsLiked(false);
        }
      }
    };

    checkLocalInteraction();
  }, [coupon.coupon_id]);

  const handleLike = async () => {
    if (!canInteract) return;

    try {
      const statsDocRef = doc(db, 'statistics', coupon.coupon_id);
      await setDoc(
        statsDocRef,
        {
          coupon_id: coupon.coupon_id,
          store_name_english: coupon.store_name_english,
          promo_code: coupon.promo_code,
          counts_positive_rating: increment(1),
          time_last_positive_rating: serverTimestamp(),
        },
        { merge: true }
      );

      localStorage.setItem(`last_interaction_${coupon.coupon_id}`, Date.now().toString());
      localStorage.setItem(`last_interaction_type_${coupon.coupon_id}`, 'like');
      setIsLiked(true);
      setIsDisliked(false);
      setCanInteract(false);
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const handleDislike = async () => {
    if (!canInteract) return;

    try {
      const statsDocRef = doc(db, 'statistics', coupon.coupon_id);
      await setDoc(
        statsDocRef,
        {
          coupon_id: coupon.coupon_id,
          store_name_english: coupon.store_name_english,
          promo_code: coupon.promo_code,
          counts_negative_rating: increment(1),
          time_last_negative_rating: serverTimestamp(),
        },
        { merge: true }
      );

      localStorage.setItem(`last_interaction_${coupon.coupon_id}`, Date.now().toString());
      localStorage.setItem(`last_interaction_type_${coupon.coupon_id}`, 'dislike');
      setIsDisliked(true);
      setIsLiked(false);
      setCanInteract(false);
    } catch (error) {
      console.error('Error updating dislike:', error);
    }
  };

  const handleTrackClick = async () => {
    try {
      const statsDocRef = doc(db, 'statistics', coupon.coupon_id); // Reference to the document
      await setDoc(
        statsDocRef,
        {
          coupon_id: coupon.coupon_id,
          store_name_english: coupon.store_name_english,
          promo_code: coupon.promo_code, // Include the promo code
          counts_link_click: increment(1), // Increment the click count
          time_last_link_click: serverTimestamp(), // Set the current timestamp
        },
        { merge: true } // Merge with existing data if the document exists
      );
    } catch (error) {
      console.error('Error tracking coupon click:', error);
    }
  };

  const handleCopyCode = async () => {
    try {
      const statsDocRef = doc(db, 'statistics', coupon.coupon_id); // Reference to the document
      await setDoc(
        statsDocRef,
        {
          coupon_id: coupon.coupon_id,
          store_name_english: coupon.store_name_english,
          promo_code: coupon.promo_code, // Include the promo code
          counts_code_copy: increment(1), // Increment the copy count
          time_last_code_copy: serverTimestamp(), // Set the current timestamp
        },
        { merge: true } // Merge with existing data if the document exists
      );
    } catch (error) {
      console.error('Error tracking code copy:', error);
    }
  };

  return {
    isLiked,
    isDisliked,
    canInteract,
    handleLike,
    handleDislike,
    handleTrackClick,
    handleCopyCode, // Expose handleCopyCode
  };
};

export default useCouponInteraction;